import type { BobState } from "../types";
import { applyRepairAction, escalateFailure, updateFailure } from "../lib/engine/failures";
import { STAGE_LABELS } from "../lib/engine/pipeline";

interface Props {
  state: BobState;
  onUpdate: (partial: Partial<BobState>) => void;
  toast: (msg: string) => void;
}

export function FailureRecoveryPanel({ state, onUpdate, toast }: Props) {
  const compact = state.presentationMode;
  const open = state.failures.filter((f) => f.status !== "resolved");

  function handleRepair(failureId: string, actionId: string) {
    const failure = state.failures.find((f) => f.id === failureId);
    if (!failure) return;
    const result = applyRepairAction(failure, actionId);
    onUpdate({
      failures: updateFailure(state.failures, failureId, () => result.failure),
    });
    toast(result.message);
  }

  function handleEscalate(failureId: string) {
    onUpdate({
      failures: updateFailure(state.failures, failureId, escalateFailure),
    });
    toast(compact ? "Escalated" : "Failure escalated to BOB for manual review");
  }

  return (
    <section className="surface glass-panel">
      <h2>{compact ? "Recovery" : "Failure Recovery"}</h2>
      <p className="lead">
        {compact
          ? "Repair or escalate failures."
          : "Seeded failure cards with deterministic repair and escalation actions — all local simulation."}
      </p>

      <div className="failure-list">
        {state.failures.map((f) => (
          <article
            key={f.id}
            className={`failure-card severity-${f.severity} status-${f.status}`}
          >
            <div className="failure-card-head">
              <h3>{f.title}</h3>
              <span className={`chip ${f.status === "resolved" ? "ok" : f.status === "escalated" ? "locked" : ""}`}>
                {f.status}
              </span>
            </div>
            {!compact && <p className="failure-desc">{f.description}</p>}
            <div className="pipeline-meta">
              <span className={`chip risk-${f.severity === "critical" || f.severity === "high" ? "high" : f.severity === "medium" ? "medium" : "low"}`}>
                {f.severity}
              </span>
              <span className="chip">{STAGE_LABELS[f.stage]}</span>
            </div>
            {f.status !== "resolved" && f.status !== "escalated" && (
              <>
                <div className="repair-actions">
                  {f.repairActions.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className="btn secondary"
                      onClick={() => handleRepair(f.id, a.id)}
                      title={a.description}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
                <div className="btn-row">
                  <button type="button" className="btn gold" onClick={() => handleEscalate(f.id)}>
                    Escalate
                  </button>
                </div>
              </>
            )}
          </article>
        ))}
      </div>

      {open.length === 0 && (
        <p className="lead">{compact ? "All clear." : "All failures resolved or escalated — pipeline clear."}</p>
      )}
    </section>
  );
}
